/**
 * React Query hooks for the Pipeline & Facility AI drafts (batch 6,
 * fieldiq-api#1236). Keys start ['pipelineFacility'] like every PF hook, so a
 * PF write elsewhere refreshes these too; reads spread
 * LOAD_FAILURE_QUERY_OPTIONS (a 403 is an answer, not a flake). A run, an
 * approve and a dismiss each invalidate the PF prefix — the register, the
 * stored run and the Overview's pending count all move.
 *
 * Approve and dismiss go through the PF doors (POST /pipeline-facility/ai/
 * digests/:id/approve | /dismiss), which take an edited narrative; the shared
 * /agent-runs doors do not.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../api/client';
import { LOAD_FAILURE_QUERY_OPTIONS } from '../lib/load-failure';
import { invalidatePf } from './usePipelineFacility';
import type { PfAiApproveBody, PfAiRunResult, PfAiStoredRun } from '../types/pf-ai';

/** `{ data }` → data, or null. Never the envelope. */
function unwrap<T>(body: unknown): T | null {
  if (body === null || typeof body !== 'object' || Array.isArray(body)) return null;
  const d = (body as { data?: unknown }).data;
  return d === undefined || d === null ? null : (d as T);
}

export const pfAiKeys = {
  digests: (agent?: string, jobId?: string) => ['pipelineFacility', 'ai', 'digests', agent ?? '', jobId ?? ''] as const,
  digest: (id: string) => ['pipelineFacility', 'ai', 'digest', id] as const,
};

export function usePfAiDigests(filters: { agent?: string; jobId?: string } = {}, enabled = true) {
  const { agent, jobId } = filters;
  return useQuery({
    queryKey: pfAiKeys.digests(agent, jobId),
    queryFn: async () => {
      const params: { agent?: string; jobId?: string } = {};
      if (agent) params.agent = agent;
      if (jobId) params.jobId = jobId;
      const d = unwrap<PfAiStoredRun[]>((await api.pipelineFacility.ai.digests(Object.keys(params).length ? params : undefined)).data);
      return Array.isArray(d) ? d : [];
    },
    enabled,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

export function usePfAiDigest(id: string | null | undefined, enabled = true) {
  return useQuery({
    queryKey: pfAiKeys.digest(id ?? ''),
    queryFn: async () => unwrap<PfAiStoredRun>((await api.pipelineFacility.ai.digest(id as string)).data),
    enabled: enabled && !!id,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

export type PfAiRunRequest =
  | { agent: 'pf_daily_summary'; jobId: string; workDate?: string }
  | { agent: 'pf_ticket_preflight'; ticketId: string }
  | { agent: 'pf_ticket_preflight'; jobId: string }
  | { agent: 'pf_compliance_digest' };

export function useRunPfAi() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (req: PfAiRunRequest): Promise<PfAiRunResult | null> => {
      const ai = api.pipelineFacility.ai;
      let res;
      if (req.agent === 'pf_daily_summary') {
        res = await ai.dailySummary({ job_id: req.jobId, ...(req.workDate ? { work_date: req.workDate } : {}) });
      } else if (req.agent === 'pf_compliance_digest') {
        res = await ai.complianceDigest();
      } else if ('ticketId' in req) {
        res = await ai.ticketPreflight(req.ticketId);
      } else {
        res = await ai.jobPreflight(req.jobId);
      }
      return unwrap<PfAiRunResult>(res.data);
    },
    onSuccess: () => invalidatePf(qc),
  });
}

export function useReviewPfAi() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (req: { id: string; action: 'approve'; edit?: PfAiApproveBody } | { id: string; action: 'dismiss' }) => {
      const res = req.action === 'approve'
        ? await api.pipelineFacility.ai.approve(req.id, req.edit ?? {})
        : await api.pipelineFacility.ai.dismiss(req.id);
      return unwrap<PfAiStoredRun>(res.data);
    },
    onSettled: () => invalidatePf(qc),
  });
}
